import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';
import {
  DateInput,
  Select,
  SelectOption,
  SelectSizeVariant,
} from '@payaca/ambrosia';
import {
  DateFormats,
  getInternationalDateFormatByLocale,
} from '@payaca/helpers/internationalHelper';
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns';
import { FC } from 'react';
import {
  type TimelogReportDatePeriod,
  useListedTimelogsContext,
} from './listedTimelogsContext';

const datePeriodOptions: SelectOption<TimelogReportDatePeriod>[] = [
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'This week', value: 'thisWeek' },
  { label: 'Last week', value: 'lastWeek' },
  { label: 'This month', value: 'thisMonth' },
  { label: 'Last month', value: 'lastMonth' },
  { label: 'Custom', value: 'custom' },
];

// Date-period filter for the account-wide timelogs report. The GraphQL
// `periodStart`/`periodEnd` are DateTime (ISO) strings; the pickers work in
// `Date`, so we convert at the boundary and snap to whole days (start-of-day
// lower bound, end-of-day upper) so the chosen days are fully included.
export const DateFilter: FC = () => {
  const { input, setFilter, datePeriod, setDatePeriod } =
    useListedTimelogsContext();
  const { data: regionPreferences } = useGetMyRegionPreferences();

  const periodStart = input?.periodStart ? new Date(input.periodStart) : null;
  const periodEnd = input?.periodEnd ? new Date(input.periodEnd) : null;

  // Format the displayed date in the account's regional format, matching the
  // table columns (rather than the browser locale the picker defaults to).
  // MID = readable month name (e.g. "20 Jun 2026" / "Jun 20 2026" in the US),
  // consistent with the table's "24 Jun" style rather than numeric dd/MM.
  const dateFormat = getInternationalDateFormatByLocale(
    DateFormats.MID,
    regionPreferences?.locale
  );
  const valueFormatter = (value: Date) => format(value, dateFormat);

  const setPeriod = (period: TimelogReportDatePeriod) => {
    setDatePeriod(period);
    if (period === 'custom') return;

    const now = new Date();
    let start: Date;
    let end: Date;

    switch (period) {
      case 'today':
        start = startOfDay(now);
        end = endOfDay(now);
        break;
      case 'yesterday': {
        const yesterday = subDays(now, 1);
        start = startOfDay(yesterday);
        end = endOfDay(yesterday);
        break;
      }
      case 'thisWeek':
        start = startOfWeek(now, { weekStartsOn: 1 });
        end = endOfWeek(now, { weekStartsOn: 1 });
        break;
      case 'lastWeek': {
        const lastWeek = subWeeks(now, 1);
        start = startOfWeek(lastWeek, { weekStartsOn: 1 });
        end = endOfWeek(lastWeek, { weekStartsOn: 1 });
        break;
      }
      case 'thisMonth':
        start = startOfMonth(now);
        end = endOfMonth(now);
        break;
      case 'lastMonth': {
        const lastMonth = subMonths(now, 1);
        start = startOfMonth(lastMonth);
        end = endOfMonth(lastMonth);
        break;
      }
    }

    setFilter({
      periodStart: start.toISOString(),
      periodEnd: end.toISOString(),
    });
  };

  return (
    <div className="flex flex-row flex-wrap items-center gap-2">
      <Select<TimelogReportDatePeriod, false>
        aria-label="Date period"
        options={datePeriodOptions}
        value={datePeriod}
        onChange={setPeriod}
        sizeVariant={SelectSizeVariant.SM}
        className="w-32"
      />
      {/* The `key` forces a remount when the bound changes or is cleared:
          ambrosia's DateInput passes `undefined` to its input for a null value,
          which makes React treat it as uncontrolled and keep stale text, so
          "Clear dates" wouldn't visually reset the field without this. */}
      <DateInput
        key={input?.periodStart ?? 'from-empty'}
        placeholder="From"
        value={periodStart}
        valueFormatter={valueFormatter}
        onChange={(date) => {
          setDatePeriod('custom');
          setFilter({ periodStart: startOfDay(date).toISOString() });
        }}
        sizeVariant="sm"
        className="w-40"
      />
      <DateInput
        key={input?.periodEnd ?? 'to-empty'}
        placeholder="To"
        value={periodEnd}
        valueFormatter={valueFormatter}
        onChange={(date) => {
          setDatePeriod('custom');
          setFilter({ periodEnd: endOfDay(date).toISOString() });
        }}
        sizeVariant="sm"
        className="w-40"
      />
    </div>
  );
};
